import { z } from 'zod';
import {
  spoItemSchema,
  spoItemIntlSchema,
  spoOptionIntlSchema,
  spoOptionSchema,
} from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/spo.ts';
import { CustomEventWithoutDetail, html, View } from 'rune-ts';
import { Input_TextEditor } from '@services/MarppleShopStudio/shared/component/atom/Input/Input_TextEditor.ts';
import { formKeyMap } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/shared.ts';
import klass from './Input_AddOption.module.scss';
import { shareFont } from '@services/MarppleShop/shared/typography';
import {
  cleanCommaString,
  validateDuringInput,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_AddOption/helpers/input-helper.ts';
import { Localized } from '@shared/type';
import { unEscaper } from '@shared/utils/escape.ts';

export class ChangeOption extends CustomEventWithoutDetail {}
export type SpoOption = z.infer<typeof spoOptionSchema>;
export type SpoIntlOption = z.infer<typeof spoOptionIntlSchema>;
export type AnySpoOption = SpoOption | SpoIntlOption;
export type SpoItem = z.infer<typeof spoItemSchema>;
export type SpoItemIntl = z.infer<typeof spoItemIntlSchema>;
export type AnySpoItem = SpoItem | SpoItemIntl;

export interface InputOptionInterface {
  dispatchChangeOptionEvent(): void;
  cleanCommaString(input: string): string;
  validateDuringInput(input: string): string;
  isAllEmpty(): boolean;
  isCompleted(): boolean;
  isCompleted(): boolean;
  getOptionInfo(): AnySpoOption;
  removeOptionInfo(): this;
  getViews(): Partial<Localized<{ name: Input_TextEditor; value: Input_TextEditor }>>;
}

export class InputOption<TSpoOption extends SpoOption = SpoOption>
  extends View<TSpoOption>
  implements InputOptionInterface
{
  private optionName = new Input_TextEditor({
    name: formKeyMap.spo_items,
    value: unEscaper(this.data.name),
    title: '옵션명',
    onInput: this.dispatchChangeOptionEvent.bind(this),
    font_klass: shareFont('14_medium'),
    option: {
      placeholder: '예시) 컬러',
    },
  });

  private optionValue = new Input_TextEditor({
    name: formKeyMap.spo_items,
    value: unEscaper(this.data._.spo_option_values.map(({ name }) => name).join(',')),
    title: '옵션값',
    onInput: () => {
      const currentValue = this.optionValue.getValue();
      const validatedValue = this.validateDuringInput(currentValue);

      // 값이 실제로 변경된 경우에만 setValue 호출
      if (currentValue !== validatedValue) {
        this.optionValue.setValue(validatedValue);
      }

      this.dispatchChangeOptionEvent();
    },
    font_klass: shareFont('14_medium'),
    option: {
      placeholder: '예시) 블랙, 블루 등 콤마 ‘,’로 구분',
    },
  });

  override template() {
    return html` <div class="${klass.option}">${this.optionName} ${this.optionValue}</div> `;
  }

  dispatchChangeOptionEvent() {
    this.dispatchEvent(ChangeOption, {
      bubbles: true,
    });
  }

  cleanCommaString(input: string): string {
    return cleanCommaString(input);
  }

  validateDuringInput(input: string): string {
    return validateDuringInput(input);
  }

  getOptionInfo(): SpoOption {
    this.optionValue.setValue(this.cleanCommaString(this.optionValue.getValue()));
    return {
      name: this.optionName.getValue(),
      _: {
        spo_option_values: this.optionValue
          .getValue()
          .split(',')
          .map((value) => ({
            name: value,
          })),
      },
    };
  }

  isAllEmpty(): boolean {
    return !this.optionName.getValue() && !this.optionValue.getValue();
  }

  isCompleted(): boolean {
    return !!this.optionName.getValue() && !!this.optionValue.getValue();
  }

  removeOptionInfo() {
    this.data.name = '';
    this.data._.spo_option_values = [];
    this.optionName.setValue('');
    this.optionValue.setValue('');
    this.redraw();
    return this;
  }

  getViews() {
    return {
      kr: {
        name: this.optionName,
        value: this.optionValue,
      },
      jp: undefined,
      en: undefined,
    };
  }
}
